import { ref } from 'vue'
import cuentasBancariasServicio from '@/services/finanzas/cuentasBancariasServicio'
import movimientosServicio from '@/services/finanzas/movimientosServicio'

/**
 * Composable para el tablero financiero
 */
export function useTableroFinanciero() {
  const resumen = ref(null)
  const flujoCajaProyectado = ref([])
  const cargando = ref(false)
  const error = ref(null)

  // Filtros de fecha
  const hoy = new Date()
  const inicioMesActual = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
  const finMesActual = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)

  const filtros = ref({
    fecha_inicio: inicioMesActual.toISOString().split('T')[0],
    fecha_fin: finMesActual.toISOString().split('T')[0]
  })

  /**
   * Cargar resumen del tablero
   */
  const cargarResumen = async (params = {}) => {
    cargando.value = true
    error.value = null
    try {
      const fechaInicio = params.fecha_inicio || filtros.value.fecha_inicio
      const fechaFin = params.fecha_fin || filtros.value.fecha_fin

      // Cargar datos en paralelo
      const [cuentas, resumenIngresos, resumenEgresos] = await Promise.all([
        cuentasBancariasServicio.listar(),
        movimientosServicio.resumenIngresos({ fecha_inicio: fechaInicio, fecha_fin: fechaFin }),
        movimientosServicio.resumenEgresos({ fecha_inicio: fechaInicio, fecha_fin: fechaFin })
      ])

      // Calcular totales
      const saldoTotal = cuentas.reduce((sum, c) => sum + Number(c.saldo_actual || 0), 0)
      const ingresosPeriodo = Number(resumenIngresos.total || 0)
      const gastosPeriodo = Number(resumenEgresos.total || 0)
      const balancePeriodo = ingresosPeriodo - gastosPeriodo

      // Calcular promedios y conteos
      const cantidadIngresos = Number(resumenIngresos.cantidad || 0)
      const cantidadGastos = Number(resumenEgresos.cantidad || 0)
      const promedioIngresos = cantidadIngresos > 0 ? ingresosPeriodo / cantidadIngresos : 0
      const promedioGastos = cantidadGastos > 0 ? gastosPeriodo / cantidadGastos : 0

      // Formatear gastos por categoría
      const gastosPorCategoria = (resumenEgresos.por_categoria || []).map(item => ({
        categoria: item.categoria?.nombre || 'Sin categoría',
        total: Number(item.total || 0)
      }))

      // Categoría con más gastos
      const categoriaTopGasto = gastosPorCategoria.length > 0
        ? gastosPorCategoria.reduce((max, cat) => cat.total > max.total ? cat : max, gastosPorCategoria[0])
        : null

      // Formatear ingresos por categoría
      const ingresosPorCategoria = (resumenIngresos.por_categoria || []).map(item => ({
        categoria: item.categoria?.nombre || 'Sin categoría',
        total: Number(item.total || 0)
      }))

      // Categoría con más ingresos
      const categoriaTopIngreso = ingresosPorCategoria.length > 0
        ? ingresosPorCategoria.reduce((max, cat) => cat.total > max.total ? cat : max, ingresosPorCategoria[0])
        : null

      resumen.value = {
        saldo_total: saldoTotal,
        ingresos_periodo: ingresosPeriodo,
        gastos_periodo: gastosPeriodo,
        balance_periodo: balancePeriodo,
        cantidad_ingresos: cantidadIngresos,
        cantidad_gastos: cantidadGastos,
        promedio_ingresos: promedioIngresos,
        promedio_gastos: promedioGastos,
        categoria_top_ingreso: categoriaTopIngreso,
        categoria_top_gasto: categoriaTopGasto,
        cuentas_por_cobrar: 0, // TODO: Implementar cuando exista el módulo
        cuentas_por_pagar: 0,  // TODO: Implementar cuando exista el módulo
        ingresos_vs_gastos: [], // TODO: Implementar histórico de 6 meses
        gastos_por_categoria: gastosPorCategoria,
        ingresos_por_categoria: ingresosPorCategoria,
        cuentas_bancarias: cuentas
      }

      return resumen.value
    } catch (err) {
      error.value = err.mensaje || 'Error al cargar resumen del tablero'
      console.error(error.value, err)
      return null
    } finally {
      cargando.value = false
    }
  }

  /**
   * Cargar flujo de caja proyectado
   */
  const cargarFlujoCajaProyectado = async (meses = 3) => {
    cargando.value = true
    error.value = null
    try {
      const respuesta = await tableroServicio.obtenerFlujoCajaProyectado(meses)
      flujoCajaProyectado.value = respuesta
      return respuesta
    } catch (err) {
      error.value = err.mensaje || 'Error al cargar flujo de caja proyectado'
      console.error(error.value, err)
      return []
    } finally {
      cargando.value = false
    }
  }

  /**
   * Recargar todos los datos
   */
  const recargarTodo = async () => {
    await Promise.all([
      cargarResumen(),
      cargarFlujoCajaProyectado()
    ])
  }

  return {
    resumen,
    flujoCajaProyectado,
    cargando,
    error,
    filtros,
    cargarResumen,
    cargarFlujoCajaProyectado,
    recargarTodo
  }
}

export default useTableroFinanciero
